import { InboundMessage, OutboundMessage, PendingCallInfo } from '../internal-types';

/**
 * Facilitates communication between the dashboard and app's iframe.
 */

const TAG = '[Dashboard SDK|Bridge]';

export abstract class BridgeBase {

  protected postMessage: (msg: OutboundMessage) => void;
  private pendingCalls: Map<number, PendingCallInfo> = new Map();
  private callCount = 0;

  constructor(window: Window) { }

  sendMessage<T>(message: OutboundMessage, persistentCallback?: (...args) => void): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      // Increment call count and assign call Id
      message.callId = ++this.callCount;

      // Transient requests do not expect a response. In this case we don't store pending call data.
      if (!message.transient) {
        // Persistent messages will resolve multiple times. Therefore they cannot use promises,
        // as promises are single-use. For persistent calls, we pass a callback.
        if (message.persistent) {
          this.pendingCalls.set(message.callId,
            { resolve: persistentCallback, reject: persistentCallback, persistent: message.persistent });
        } else {
          this.pendingCalls.set(message.callId, { resolve, reject, persistent: message.persistent });
        }
      }

      if (this.postMessage) {
        this.postMessage(message);
      }
    });
  }

  /**
   * Receives and parses a message from the dashboard.
   * @param event - An event coming from calling `postMessage` on the App's frame.
   * @returns Whether to allow the event to be handled by another event handler.
   */
  protected receiveMessage(event: MessageEvent): boolean {
    if (event && event.type === 'message' && typeof event.data === 'string') {
      try {
        const response = JSON.parse(event.data) as InboundMessage;
        return this.dispatchResponse(response);
      } catch (e) {
        // ignore
      }
    }
  }

  /**
   * Decides what to do with an incoming message.
   * @param response - A dashboard's response to App's call.
   * @returns Whether to allow the event to ne handled by another event handler.
   */
  protected dispatchResponse(response: InboundMessage): boolean {
    if (response.callId && typeof response.success === 'boolean') {
      const { resolve, reject, persistent } = this.pendingCalls.get(response.callId);
      if (resolve && reject) {

        // If the call is not persistent, delete the pending call info from the map.
        if (!persistent) {
          this.pendingCalls.delete(response.callId);
        }

        if (response.success) {
          resolve(response.data);
        } else {
          reject(response.data);
        }
        return false;
      }
    }
  }

  abstract setWindowEventHandler(eventName: string, handler: (event: Event) => void): void;
}
